import { UUID } from "./outings";

export type ChatKind = "friend" | "squad" | "resort";
export type ChatSource = {
  id: string;
  name: string;
  revision: number;
  conversationId: string | null;
  memberRevision: number;
  joined: boolean;
  expiresAt?: string;
};
export type ChatSources = {
  owner: string;
  preferences: { friendMessagesEnabled: boolean; revision: number };
  friends: ChatSource[];
  squads: ChatSource[];
  resorts: ChatSource[];
};
export type ChatSummary = {
  id: string;
  title: string;
  kind: ChatKind;
  revision: number;
  updatedAt: string;
  memberRevision: number;
  muted: boolean;
  unread: boolean;
};
export type ChatMessage = {
  id: string;
  sequence: number;
  revision: number;
  senderId: string;
  senderName: string;
  body: string;
  createdAt: string;
  updatedAt: string;
  deleted: boolean;
  hidden: boolean;
  own: boolean;
  canEdit: boolean;
};
export type ChatConversation = Omit<ChatSummary, "unread" | "updatedAt"> & {
  sourceRevision: number;
  readSequence: number;
  latestSequence: number;
  canSend: boolean;
  canPin: boolean;
  accessExpiresAt: string | null;
  pin: { id: string; revision: number } | null;
};
export type ChatPage = {
  owner: string;
  conversation: ChatConversation;
  messages: ChatMessage[];
  before: number | null;
  checkedAt: string;
};
export type ChatCase = {
  id: string;
  messageId: string;
  revision: number;
  state: "open" | "closed" | "appealed";
  decision: string | null;
  publicReason: string | null;
  updatedAt: string;
  own: boolean;
  canAppeal: boolean;
};
export type ChatModerationCase = {
  id: string;
  revision: number;
  state: ChatCase["state"];
  evidence: string;
  evidenceRevision: number;
  decision: string | null;
  publicReason: string | null;
  privateReason: string | null;
  appeal: string | null;
  messageRevision: number;
  currentBody: string;
  deletedAt: string | null;
  moderationStatus: string;
  reports: { reason: string; detail: string; createdAt: string }[];
  history: {
    action: string;
    caseRevision: number;
    messageRevision: number;
    createdAt: string;
    publicReason?: string | null;
    privateReason?: string | null;
  }[];
};
type Mutation = { mutationId: string };
type Member = Mutation & { id: string; memberRevision: number };
export type ChatCommand =
  | (Mutation & {
      action: "join";
      kind: ChatKind;
      sourceId: string;
      sourceRevision: number;
      memberRevision: number;
    })
  | (Mutation & { action: "preferences"; revision: number; enabled: boolean })
  | (Member & { action: "send"; messageId: string; body: string })
  | (Member & {
      action: "edit";
      messageId: string;
      revision: number;
      body: string;
    })
  | (Member & { action: "delete"; messageId: string; revision: number })
  | (Member & { action: "read"; sequence: number })
  | (Member & { action: "mute"; muted: boolean })
  | (Member & { action: "leave" })
  | (Member & {
      action: "pin";
      revision: number;
      outingId: string | null;
      outingRevision: number | null;
    });
export type ChatReport =
  | {
      action: "report";
      messageId: string;
      revision: number;
      reason: "spam" | "abuse" | "privacy" | "unsafe" | "other";
      detail: string;
    }
  | { action: "appeal"; caseId: string; revision: number; detail: string };
export type ChatDecision = Mutation & {
  action: "hide" | "restore" | "no_action";
  caseId: string;
  revision: number;
  messageRevision: number;
  publicReason: string;
  privateReason: string;
};
const record = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === "object" && !Array.isArray(v);
const integer = (v: unknown, min = 0): v is number =>
  typeof v === "number" && Number.isInteger(v) && v >= min && v <= 999_999_999;
const uuid = (v: unknown): v is string => typeof v === "string" && UUID.test(v);
export const chatText = (
  v: unknown,
  max = 2000,
  bytes = 4096,
  min = 1,
): v is string =>
  typeof v === "string" &&
  [...v].length <= max &&
  [...v.trim()].length >= min &&
  new TextEncoder().encode(v).byteLength <= bytes;
const exact = (v: Record<string, unknown>, fields: string[]) =>
  Object.keys(v).length === fields.length &&
  fields.every((k) => Object.hasOwn(v, k));
export function validChatCommand(v: unknown): v is ChatCommand {
  if (!record(v) || !uuid(v.mutationId)) return false;
  if (v.action === "join")
    return (
      exact(v, [
        "action",
        "mutationId",
        "kind",
        "sourceId",
        "sourceRevision",
        "memberRevision",
      ]) &&
      ["friend", "squad", "resort"].includes(String(v.kind)) &&
      uuid(v.sourceId) &&
      integer(v.sourceRevision, 1) &&
      integer(v.memberRevision)
    );
  if (v.action === "preferences")
    return (
      exact(v, ["action", "mutationId", "revision", "enabled"]) &&
      integer(v.revision) &&
      typeof v.enabled === "boolean"
    );
  if (!uuid(v.id) || !integer(v.memberRevision, 1)) return false;
  const base = ["action", "mutationId", "id", "memberRevision"];
  switch (v.action) {
    case "send":
      return (
        exact(v, [...base, "messageId", "body"]) &&
        uuid(v.messageId) &&
        v.messageId === v.mutationId &&
        chatText(v.body)
      );
    case "edit":
      return (
        exact(v, [...base, "messageId", "revision", "body"]) &&
        uuid(v.messageId) &&
        integer(v.revision, 1) &&
        chatText(v.body)
      );
    case "delete":
      return (
        exact(v, [...base, "messageId", "revision"]) &&
        uuid(v.messageId) &&
        integer(v.revision, 1)
      );
    case "read":
      return exact(v, [...base, "sequence"]) && integer(v.sequence);
    case "mute":
      return exact(v, [...base, "muted"]) && typeof v.muted === "boolean";
    case "leave":
      return exact(v, base);
    case "pin":
      return (
        exact(v, [...base, "revision", "outingId", "outingRevision"]) &&
        integer(v.revision, 1) &&
        (v.outingId === null
          ? v.outingRevision === null
          : uuid(v.outingId) && integer(v.outingRevision, 1))
      );
    default:
      return false;
  }
}
export function validChatReport(v: unknown): v is ChatReport {
  if (!record(v) || !integer(v.revision, 1)) return false;
  if (v.action === "appeal")
    return (
      exact(v, ["action", "caseId", "revision", "detail"]) &&
      uuid(v.caseId) &&
      chatText(v.detail, 1000, 4000, 2)
    );
  return (
    v.action === "report" &&
    exact(v, ["action", "messageId", "revision", "reason", "detail"]) &&
    uuid(v.messageId) &&
    ["spam", "abuse", "privacy", "unsafe", "other"].includes(
      String(v.reason),
    ) &&
    chatText(v.detail, 1000, 4000, 0)
  );
}
export function validChatDecision(v: unknown): v is ChatDecision {
  return (
    record(v) &&
    exact(v, [
      "action",
      "mutationId",
      "caseId",
      "revision",
      "messageRevision",
      "publicReason",
      "privateReason",
    ]) &&
    ["hide", "restore", "no_action"].includes(String(v.action)) &&
    uuid(v.mutationId) &&
    uuid(v.caseId) &&
    integer(v.revision, 1) &&
    integer(v.messageRevision, 1) &&
    chatText(v.publicReason, 500, 2000, 2) &&
    chatText(v.privateReason, 1000, 4000, 0)
  );
}
export function chatError(message: string): { status: number; error: string } {
  if (/rate_limit/.test(message))
    return {
      status: 429,
      error:
        "You have sent several messages or reports. Please wait before trying again.",
    };
  if (/conflict|mutation_reused|edit_expired/.test(message))
    return {
      status: 409,
      error: "This chat or message changed. Refresh it and review your action.",
    };
  if (/unavailable|disabled|pin_audience/.test(message))
    return {
      status: 403,
      error:
        "This action is no longer available. Check the current membership, message settings or outing invitations.",
    };
  if (/invalid|22P02|23514/.test(message))
    return { status: 400, error: "Check the chat details and try again." };
  if (/owner/.test(message))
    return {
      status: 403,
      error: "Your account changed. Reopen chat with the original account.",
    };
  return {
    status: 503,
    error: "Chat could not be checked. Please retry when connected.",
  };
}
export function parseChatQuery(params: URLSearchParams) {
  if ([...params.values()].some((value) => value.length === 0)) return null;
  if (
    [...params.keys()].some(
      (k) => !["view", "id", "before", "after", "time"].includes(k),
    ) ||
    [...new Set(params.keys())].some((k) => params.getAll(k).length !== 1)
  )
    return null;
  const view = params.get("view") ?? "conversations",
    id = params.get("id"),
    before = params.get("before"),
    after = params.get("after"),
    time = params.get("time");
  if (
    !["sources", "conversations", "messages", "reports"].includes(view) ||
    (id && !uuid(id)) ||
    (after && !uuid(after)) ||
    (before && !/^[1-9]\d{0,8}$/.test(before)) ||
    (time &&
      (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,6})?(?:Z|[+-]\d{2}:\d{2})$/.test(
        time,
      ) ||
        !Number.isFinite(Date.parse(time))))
  )
    return null;
  if (
    (view === "sources" && (id || before || after || time)) ||
    (view === "messages" && (!id || after || time)) ||
    (view === "conversations" && (id || before || !!after !== !!time)) ||
    (view === "reports" && (id || before || time))
  )
    return null;
  return { view, id, before: before ? Number(before) : null, after, time };
}
